// TUT-537 — o `validateSearch` de `/defesas`, em arquivo próprio (o `buscas.ts`
// está no teto da catraca de tamanho).
//
// O censo das rotas de lista (`list-route-contract.test.ts`) LÊ este arquivo:
// a rota é paginada e ordenável no servidor, então a chamada a
// `buscaOrdenavel(` tem de estar aqui.
import { buscaOrdenavel, ligado, lista, texto, type ListaOrdenavelSearch } from "./buscas";

export const TIPOS_DE_DOCUMENTO_DAS_DEFESAS = ["QUESTIONS", "IMPUGNATION", "EXPERT_REPORT"] as const;
export const OBJETOS_DAS_DEFESAS = ["INSALUBRIDADE", "PERICULOSIDADE", "ERGONOMIA"] as const;
export const RESULTADOS_DAS_DEFESAS = ["FAVORABLE", "PARTIAL", "UNFAVORABLE", "NOT_APPLICABLE"] as const;

/**
 * O estado da biblioteca na URL: a busca (`q`), a página, a ordem e cada
 * filtro. Tudo opcional, e o que está desligado não polui o endereço.
 * `adicional` é `sim` ou `nao`; os dias são `AAAA-MM-DD`.
 */
export type DefesasSearch = ListaOrdenavelSearch & {
  especial?: true;
  cliente?: string;
  documento?: string[];
  objeto?: string[];
  agente?: string[];
  funcao?: string;
  setor?: string;
  resultado?: string;
  adicional?: "sim" | "nao";
  reclamada?: string;
  comarca?: string;
  uf?: string;
  vara?: string;
  perito?: string;
  assistente?: string;
  elaborador?: string;
  de?: string;
  ate?: string;
};

/** Só os valores da lista fechada; o resto some (a URL é entrada do usuário). */
function dentroDaLista(valor: unknown, fechada: readonly string[]): string[] | undefined {
  const escolhidos = lista(valor)?.filter((item) => fechada.includes(item));
  return escolhidos?.length ? [...new Set(escolhidos)] : undefined;
}

/** O dia que existe no calendário, ou nada. */
function dia(valor: unknown): string | undefined {
  const bruto = texto(valor);
  if (!bruto || !/^\d{4}-\d{2}-\d{2}$/u.test(bruto)) return undefined;
  // Aritmética de calendário em UTC: o dia que existe volta igual (31/02 não).
  const [ano = 0, mes = 0, diaDoMes = 0] = bruto.split("-").map(Number);
  const data = new Date(Date.UTC(ano, mes - 1, diaDoMes));
  const existe = data.getUTCFullYear() === ano && data.getUTCMonth() === mes - 1 && data.getUTCDate() === diaDoMes;
  return existe ? bruto : undefined;
}

export function validarBuscaDeDefesas(busca: Record<string, unknown>): DefesasSearch {
  const validada = buscaOrdenavel("defesas", busca, {
    especial: ligado,
    cliente: texto,
    documento: (valor) => dentroDaLista(valor, TIPOS_DE_DOCUMENTO_DAS_DEFESAS),
    objeto: (valor) => dentroDaLista(valor, OBJETOS_DAS_DEFESAS),
    agente: lista,
    funcao: texto,
    setor: texto,
    resultado: (valor) => dentroDaLista(valor, RESULTADOS_DAS_DEFESAS)?.[0],
    adicional: (valor) => (valor === "sim" || valor === "nao" ? valor : undefined),
    reclamada: texto,
    comarca: texto,
    uf: (valor) => (typeof valor === "string" && /^[A-Za-z]{2}$/u.test(valor) ? valor.toUpperCase() : undefined),
    vara: texto,
    perito: texto,
    assistente: texto,
    elaborador: texto,
    de: dia,
    ate: dia,
  }) as DefesasSearch;
  // O período invertido é descartado INTEIRO: a API o recusa com 400, e a tela
  // não manda o que já sabe que não vale.
  if (validada.de && validada.ate && validada.de > validada.ate) {
    const { de: _de, ate: _ate, ...resto } = validada;
    return resto;
  }
  return validada;
}
